import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import { admin, auth } from '@/lib/api';
import { getStoredUser, clearStoredUser } from '@/lib/auth';
import { 
  Clock, LogOut, Users, Building2, Briefcase, BarChart3, 
  Loader2, RefreshCw, TrendingUp, Eye, User, Plus, Pencil, 
  Trash2, Key, UserPlus, School, AlertTriangle, Search, Download
} from 'lucide-react';
import { format } from 'date-fns';
import { CreateUserDialog, EditUserDialog, ResetPasswordDialog, DeleteUserDialog, UserDetailDialog, PricingLeadsTab, TrialPremiumTab } from '@/components/admin';
import BrandLogo from '@/components/BrandLogo';

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(getStoredUser());
  const [isLoading, setIsLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [users, setUsers] = useState([]);
  const [schools, setSchools] = useState([]);
  const [positions, setPositions] = useState([]);
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [schoolSearchQuery, setSchoolSearchQuery] = useState('');
  const [positionSearchQuery, setPositionSearchQuery] = useState('');
  
  // View detail states
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [showUserDetail, setShowUserDetail] = useState(false);
  const [showSchoolDetail, setShowSchoolDetail] = useState(false);
  
  // Create/Edit dialogs
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [showEditUser, setShowEditUser] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [showDeleteUser, setShowDeleteUser] = useState(false);
  const [showCreateSchool, setShowCreateSchool] = useState(false);
  const [showEditSchool, setShowEditSchool] = useState(false);
  const [showDeleteSchool, setShowDeleteSchool] = useState(false);
  
  // Form states
  const [userForm, setUserForm] = useState({
    email: '', password: '', name: '', role: 'toc',
    phone: '', subjects: '', availability: ''
  });
  const [editUserForm, setEditUserForm] = useState({});
  const [newPassword, setNewPassword] = useState('');
  const [schoolForm, setSchoolForm] = useState({
    name: '', address: '', contact_person: '', contact_position: '',
    contact_email: '', contact_phone: '', start_time: '08:00', end_time: '15:00', employer_id: ''
  });
  const [editSchoolForm, setEditSchoolForm] = useState({});
  
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Memoize expensive filtered lists
  const filteredUsers = useMemo(() => {
    return users.filter(u => {
      // Role filter
      if (roleFilter !== 'all' && u.role !== roleFilter) return false;
      
      // Search filter
      if (!searchQuery.trim()) return true;
      const query = searchQuery.toLowerCase();
      const nameMatch = u.name?.toLowerCase().includes(query);
      const emailMatch = u.email?.toLowerCase().includes(query);
      const schoolMatch = u.connected_schools?.some(schoolId => {
        const school = schools.find(s => s.school_id === schoolId);
        return school?.name?.toLowerCase().includes(query);
      });
      const ownedSchoolMatch = schools
        .filter(s => s.employer_id === u.user_id)
        .some(s => s.name?.toLowerCase().includes(query));
      return nameMatch || emailMatch || schoolMatch || ownedSchoolMatch;
    });
  }, [users, schools, roleFilter, searchQuery]);

  const filteredSchools = useMemo(() => {
    return schools.filter(school => {
      if (!schoolSearchQuery.trim()) return true;
      const query = schoolSearchQuery.toLowerCase();
      const nameMatch = school.name?.toLowerCase().includes(query);
      const addressMatch = school.address?.toLowerCase().includes(query);
      const codeMatch = school.school_code?.toLowerCase().includes(query);
      const owner = users.find(u => u.user_id === school.employer_id);
      const ownerMatch = owner?.name?.toLowerCase().includes(query) || owner?.email?.toLowerCase().includes(query);
      const contactNameMatch = school.contact_name?.toLowerCase().includes(query);
      const contactPositionMatch = school.contact_position?.toLowerCase().includes(query);
      const contactEmailMatch = school.contact_email?.toLowerCase().includes(query);
      const contactPhoneMatch = school.contact_phone?.toLowerCase().includes(query);
      const archivedMatch = (school.archived ? 'archived' : 'active').includes(query);
      const startTimeMatch = school.start_time?.toLowerCase().includes(query);
      const endTimeMatch = school.end_time?.toLowerCase().includes(query);
      return nameMatch || addressMatch || codeMatch || ownerMatch ||
             contactNameMatch || contactPositionMatch || contactEmailMatch || contactPhoneMatch ||
             archivedMatch || startTimeMatch || endTimeMatch;
    });
  }, [schools, users, schoolSearchQuery]);

  const filteredPositions = useMemo(() => {
    return positions.filter(pos => {
      // Status filter
      if (statusFilter !== 'all' && pos.status !== statusFilter) return false;
      
      // Search filter
      if (!positionSearchQuery.trim()) return true;
      const query = positionSearchQuery.toLowerCase();
      const schoolMatch = pos.school_name?.toLowerCase().includes(query);
      const gradeMatch = pos.grade_level?.toLowerCase().includes(query);
      const dateMatch = pos.dates?.some(d => d.includes(query)) || pos.date?.includes(query);
      const descMatch = pos.description?.toLowerCase().includes(query);
      const acceptedMatch = pos.accepted_by_name?.toLowerCase().includes(query);
      return schoolMatch || gradeMatch || dateMatch || descMatch || acceptedMatch;
    });
  }, [positions, statusFilter, positionSearchQuery]);

  const fetchData = useCallback(async () => {
    try {
      const [analyticsRes, usersRes, schoolsRes, positionsRes] = await Promise.all([
        admin.getAnalytics(),
        admin.getUsers({ role: roleFilter !== 'all' ? roleFilter : undefined, limit: 1000 }),
        admin.getSchools({ limit: 1000 }),
        admin.getPositions({ status: statusFilter !== 'all' ? statusFilter : undefined, limit: 1000 })
      ]);
      
      setAnalytics(analyticsRes.data);
      setUsers(usersRes.data.users);
      setSchools(schoolsRes.data.schools);
      setPositions(positionsRes.data.positions);
    } catch (error) {
      // Silent fail - data will be retried on next poll
      if (error.response?.status === 403) {
        toast.error('Access denied. Subscription manager only.');
        navigate('/login');
      }
    } finally {
      setIsLoading(false);
    }
  }, [roleFilter, statusFilter, navigate]);

  useEffect(() => {
    if (user?.role !== 'subscription_manager') {
      navigate('/login');
      return;
    }
    fetchData();
  }, [user, fetchData, navigate]);

  // User handlers
  const handleViewUser = async (userId) => {
    try {
      const res = await admin.getUserDetails(userId);
      setSelectedUser(res.data);
      setShowUserDetail(true);
    } catch (error) {
      toast.error('Failed to load user details');
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await admin.createUser(userForm);
      toast.success('User created successfully');
      setShowCreateUser(false);
      setUserForm({ email: '', password: '', name: '', role: 'toc', phone: '', subjects: '', availability: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenEditUser = (u) => {
    setEditUserForm({
      user_id: u.user_id,
      name: u.name || '',
      email: u.email || '',
      phone: u.phone || '',
      subjects: u.subjects || '',
      availability: u.availability || '',
      role: u.role
    });
    setShowEditUser(true);
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await admin.updateUser(editUserForm.user_id, editUserForm);
      toast.success('User updated successfully');
      setShowEditUser(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenResetPassword = (u) => {
    setSelectedUser(u);
    setNewPassword('');
    setShowResetPassword(true);
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setIsSubmitting(true);
    try {
      await admin.resetPassword(selectedUser.user_id, newPassword);
      toast.success(`Password reset for ${selectedUser.name}`);
      setShowResetPassword(false);
      setNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenDeleteUser = (u) => {
    setSelectedUser(u);
    setShowDeleteUser(true);
  };

  const handleDeleteUser = async () => {
    setIsSubmitting(true);
    try {
      await admin.deleteUser(selectedUser.user_id);
      toast.success(`User ${selectedUser.name} deleted`);
      setShowDeleteUser(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    } finally {
      setIsSubmitting(false);
    }
  };

  // School handlers
  const handleViewSchool = async (schoolId) => {
    try {
      const res = await admin.getSchoolDetails(schoolId);
      setSelectedSchool(res.data);
      setShowSchoolDetail(true);
    } catch (error) {
      toast.error('Failed to load school details');
    }
  };

  const handleCreateSchool = async (e) => {
    e.preventDefault();
    if (!schoolForm.employer_id) {
      toast.error('Please select a school admin');
      return;
    }
    setIsSubmitting(true);
    try {
      await admin.createSchool(schoolForm);
      toast.success('School created successfully');
      setShowCreateSchool(false);
      setSchoolForm({
        name: '', address: '', contact_person: '', contact_position: '',
        contact_email: '', contact_phone: '', start_time: '08:00', end_time: '15:00', employer_id: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create school');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenEditSchool = (s) => {
    setEditSchoolForm({
      school_id: s.school_id,
      name: s.name || '',
      address: s.address || '',
      contact_person: s.contact_person || '',
      contact_position: s.contact_position || '',
      contact_email: s.contact_email || '',
      contact_phone: s.contact_phone || '',
      start_time: s.start_time || '08:00',
      end_time: s.end_time || '15:00'
    });
    setShowEditSchool(true);
  };

  const handleUpdateSchool = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await admin.updateSchool(editSchoolForm.school_id, editSchoolForm);
      toast.success('School updated successfully');
      setShowEditSchool(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update school');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenDeleteSchool = (s) => {
    setSelectedSchool(s);
    setShowDeleteSchool(true);
  };

  const handleDeleteSchool = async () => {
    setIsSubmitting(true);
    try {
      await admin.deleteSchool(selectedSchool.school_id);
      toast.success(`School ${selectedSchool.name} deleted`);
      setShowDeleteSchool(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete school');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLogout = async () => {
    try {
      await auth.logout();
    } catch (_e) {
      // Silent fail - proceed with local logout
    }
    clearStoredUser();
    navigate('/');
  };

  // Get employer users for school creation
  const employerUsers = users.filter(u => u.role === 'employer');

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <Loader2 className="h-8 w-8 animate-spin text-amber-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header */}
      <header className="bg-slate-800 text-white sticky top-0 z-50 border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BrandLogo variant="white" size="lg" />
            <Badge className="ml-2 bg-amber-500 text-slate-900">Admin</Badge>
          </div>
          
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="sm"
              onClick={fetchData}
              className="text-slate-300 hover:text-white hover:bg-slate-700"
              data-testid="refresh-data-btn"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
            <Button 
              variant="ghost" 
              size="icon"
              className="text-slate-300 hover:text-white hover:bg-slate-700"
              onClick={() => navigate('/profile')}
              data-testid="profile-btn"
            >
              <User className="h-5 w-5" />
            </Button>
            <Button 
              variant="ghost" 
              size="icon"
              className="text-slate-300 hover:text-white hover:bg-slate-700"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Analytics Overview */}
        <h1 className="text-3xl font-bold text-white font-['Manrope'] mb-6">
          Platform Management
        </h1>
        
        {analytics && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-slate-400 text-sm">Total Users</p>
                    <p className="text-3xl font-bold text-white">{analytics.users.total}</p>
                    <div className="flex gap-3 mt-2 text-xs">
                      <span className="text-emerald-400">{analytics.users.subs} Subs</span>
                      <span className="text-blue-400">{analytics.users.employers} Admins</span>
                    </div>
                  </div>
                  <Users className="h-10 w-10 text-slate-600" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-slate-400 text-sm">Total Schools</p>
                    <p className="text-3xl font-bold text-white">{analytics.schools.total}</p>
                    <div className="flex gap-2 mt-2 text-xs">
                      <span className="text-emerald-400">{analytics.schools.accounts_by_status?.active ?? 0} Active accounts</span>
                      {analytics.schools.accounts_by_status?.past_due > 0 && (
                        <span className="text-rose-400">{analytics.schools.accounts_by_status.past_due} Past due</span>
                      )}
                    </div>
                  </div>
                  <Building2 className="h-10 w-10 text-slate-600" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-slate-400 text-sm">Total Positions</p>
                    <p className="text-3xl font-bold text-white">{analytics.positions.total}</p>
                    <div className="flex gap-2 mt-2 text-xs">
                      <span className="text-emerald-400">{analytics.positions.filled} Filled</span>
                      <span className="text-blue-400">{analytics.positions.open} Open</span>
                    </div>
                  </div>
                  <Briefcase className="h-10 w-10 text-slate-600" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-slate-400 text-sm">Fill Rate</p>
                    <p className="text-3xl font-bold text-emerald-400">{analytics.positions.fill_rate}%</p>
                    <div className="flex gap-2 mt-2 text-xs">
                      <span className="text-slate-400">Last 7 days:</span>
                      <span className="text-amber-400">{analytics.recent_activity.new_positions_7d} new</span>
                    </div>
                  </div>
                  <TrendingUp className="h-10 w-10 text-slate-600" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Data Tables */}
        <Tabs defaultValue="users" className="space-y-6">
          <TabsList className="bg-slate-800 border-slate-700">
            <TabsTrigger value="users" className="data-[state=active]:bg-amber-500 data-[state=active]:text-slate-900">
              Users ({users.length})
            </TabsTrigger>
            <TabsTrigger value="schools" className="data-[state=active]:bg-amber-500 data-[state=active]:text-slate-900">
              Schools ({schools.length})
            </TabsTrigger>
            <TabsTrigger value="positions" className="data-[state=active]:bg-amber-500 data-[state=active]:text-slate-900">
              Positions ({positions.length})
            </TabsTrigger>
            <TabsTrigger value="leads" className="data-[state=active]:bg-amber-500 data-[state=active]:text-slate-900" data-testid="admin-tab-pricing-leads">
              Pricing Leads
            </TabsTrigger>
            <TabsTrigger value="trials" className="data-[state=active]:bg-amber-500 data-[state=active]:text-slate-900" data-testid="admin-tab-trial-premium">
              Trial Premium
            </TabsTrigger>
          </TabsList>

          <TabsContent value="users" className="space-y-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 justify-between">
              <div className="flex items-center gap-3 w-full sm:w-auto">
                <Select value={roleFilter} onValueChange={setRoleFilter}>
                  <SelectTrigger className="w-40 bg-slate-800 border-slate-700 text-white">
                    <SelectValue placeholder="Filter by role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Roles</SelectItem>
                    <SelectItem value="toc">Substitutes</SelectItem>
                    <SelectItem value="employer">School Admins</SelectItem>
                  </SelectContent>
                </Select>
                
                <div className="relative flex-1 sm:w-64">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Search by name, email, or school..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-400"
                    data-testid="user-search-input"
                  />
                </div>
              </div>
              
              <Button 
                onClick={() => setShowCreateUser(true)}
                className="bg-amber-500 hover:bg-amber-600 text-slate-900"
                data-testid="add-user-btn"
              >
                <UserPlus className="h-4 w-4 mr-2" />
                Add User
              </Button>
            </div>
            
            <Card className="bg-slate-800 border-slate-700">
              <ScrollArea className="h-[500px]">
                <div className="divide-y divide-slate-700">
                  {filteredUsers.map((u) => (
                    <div 
                      key={u.user_id} 
                      className="p-4 flex items-center justify-between hover:bg-slate-750"
                    >
                      <div className="flex items-center gap-4">
                        <div className="h-10 w-10 rounded-full bg-slate-700 flex items-center justify-center">
                          {u.picture ? (
                            <img src={u.picture} alt={u.name} className="h-10 w-10 rounded-full object-cover" />
                          ) : (
                            <span className="text-slate-300 font-medium">
                              {u.name?.charAt(0).toUpperCase()}
                            </span>
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-white">{u.name}</p>
                          <p className="text-sm text-slate-400">{u.email}</p>
                          {/* Show connected schools for Subs */}
                          {u.role === 'toc' && u.connected_schools?.length > 0 && (
                            <p className="text-xs text-slate-500 mt-0.5">
                              Schools: {u.connected_schools
                                .slice(0, 3)
                                .map(schoolId => schools.find(s => s.school_id === schoolId)?.name || 'Unknown')
                                .join(', ')}
                              {u.connected_schools.length > 3 && ` +${u.connected_schools.length - 3} more`}
                            </p>
                          )}
                          {/* Show owned schools for Employers */}
                          {u.role === 'employer' && (
                            <p className="text-xs text-slate-500 mt-0.5">
                              {(() => {
                                const owned = schools.filter(s => s.employer_id === u.user_id);
                                if (owned.length === 0) return 'No schools';
                                const names = owned.map(s => s.name).slice(0, 2).join(', ');
                                const extra = owned.length > 2 ? ` +${owned.length - 2} more` : '';
                                return `Owns: ${names}${extra}`;
                              })()}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge className={(() => {
                          if (u.role === 'toc') return 'bg-emerald-600';
                          if (u.role === 'subscription_manager') return 'bg-amber-600';
                          return 'bg-blue-600';
                        })()}>
                          {(() => {
                            if (u.role === 'toc') return 'Sub';
                            if (u.role === 'subscription_manager') return 'Admin';
                            return 'School Admin';
                          })()}
                        </Badge>
                        <div className="flex items-center gap-1 bg-slate-700/50 rounded-lg px-2 py-1">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="text-slate-300 hover:text-white hover:bg-slate-600 h-8 px-2"
                            onClick={() => handleViewUser(u.user_id)}
                            data-testid={`view-user-${u.user_id}`}
                            title="View Details"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          {u.role !== 'subscription_manager' && (
                            <>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                className="text-blue-400 hover:text-blue-300 hover:bg-slate-600 h-8 px-2"
                                onClick={() => handleOpenEditUser(u)}
                                data-testid={`edit-user-${u.user_id}`}
                                title="Edit User"
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                className="text-amber-400 hover:text-amber-300 hover:bg-slate-600 h-8 px-2"
                                onClick={() => handleOpenResetPassword(u)}
                                data-testid={`reset-password-${u.user_id}`}
                                title="Reset Password"
                              >
                                <Key className="h-4 w-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                className="text-red-400 hover:text-red-300 hover:bg-slate-600 h-8 px-2"
                                onClick={() => handleOpenDeleteUser(u)}
                                data-testid={`delete-user-${u.user_id}`}
                                title="Delete User"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                className="text-cyan-400 hover:text-cyan-300 hover:bg-slate-600 h-8 px-2"
                                onClick={async () => {
                                  try {
                                    const res = await adminApi.exportUserData(u.user_id);
                                    const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
                                    const url = URL.createObjectURL(blob);
                                    const a = document.createElement('a');
                                    a.href = url;
                                    a.download = `expresssub-user-data-${u.user_id}.json`;
                                    a.click();
                                    URL.revokeObjectURL(url);
                                    toast.success('User data exported');
                                  } catch (_e) {
                                    toast.error('Failed to export user data');
                                  }
                                }}
                                data-testid={`export-user-${u.user_id}`}
                                title="Export User Data"
                              >
                                <Download className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </Card>
          </TabsContent>

          <TabsContent value="schools" className="space-y-4">
            <div className="flex items-center justify-between gap-4">
              <div className="relative flex-1 sm:w-64">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search by name, address, code, contact, status..."
                  value={schoolSearchQuery}
                  onChange={(e) => setSchoolSearchQuery(e.target.value)}
                  className="pl-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-400"
                  data-testid="school-search-input"
                />
              </div>
              <Button 
                onClick={() => setShowCreateSchool(true)}
                className="bg-amber-500 hover:bg-amber-600 text-slate-900"
                data-testid="add-school-btn"
              >
                <School className="h-4 w-4 mr-2" />
                Add School
              </Button>
            </div>
            
            <Card className="bg-slate-800 border-slate-700">
              <ScrollArea className="h-[500px]">
                <div className="divide-y divide-slate-700">
                  {filteredSchools.map((school) => (
                    <div 
                      key={school.school_id} 
                      className="p-4 flex items-center justify-between hover:bg-slate-750"
                    >
                      <div>
                        <p className="font-medium text-white">{school.name}</p>
                        <p className="text-sm text-slate-400">{school.address}</p>
                        <div className="flex gap-2 mt-1">
                          <span className="text-xs text-slate-500">Code: {school.school_code}</span>
                          <span className="text-xs text-slate-500">•</span>
                          <span className="text-xs text-slate-500">{school.toc_list?.length || 0} Subs</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge className={school.archived ? 'bg-slate-600' : 'bg-emerald-600'}>
                          {school.archived ? 'Archived' : 'Active'}
                        </Badge>
                        <div className="flex items-center gap-1 bg-slate-700/50 rounded-lg px-2 py-1">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="text-slate-300 hover:text-white hover:bg-slate-600 h-8 px-2"
                            onClick={() => handleViewSchool(school.school_id)}
                            data-testid={`view-school-${school.school_id}`}
                            title="View Details"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="text-blue-400 hover:text-blue-300 hover:bg-slate-600 h-8 px-2"
                            onClick={() => handleOpenEditSchool(school)}
                            data-testid={`edit-school-${school.school_id}`}
                            title="Edit School"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="text-red-400 hover:text-red-300 hover:bg-slate-600 h-8 px-2"
                            onClick={() => handleOpenDeleteSchool(school)}
                            data-testid={`delete-school-${school.school_id}`}
                            title="Delete School"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </Card>
          </TabsContent>

          <TabsContent value="positions" className="space-y-4">
            <div className="flex items-center gap-4">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-48 bg-slate-800 border-slate-700 text-white">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="filled">Filled</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
              
              <div className="relative flex-1 sm:w-64">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search by school, grade, or date..."
                  value={positionSearchQuery}
                  onChange={(e) => setPositionSearchQuery(e.target.value)}
                  className="pl-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-400"
                  data-testid="position-search-input"
                />
              </div>
            </div>
            
            <Card className="bg-slate-800 border-slate-700">
              <ScrollArea className="h-[500px]">
                <div className="divide-y divide-slate-700">
                  {filteredPositions.map((pos) => (
                    <div 
                      key={pos.position_id} 
                      className="p-4 flex items-center justify-between hover:bg-slate-750"
                    >
                      <div>
                        <p className="font-medium text-white">{pos.school_name}</p>
                        <p className="text-sm text-slate-400">{pos.grade_level}</p>
                        <div className="flex gap-2 mt-1">
                          <span className="text-xs text-slate-500">
                            {pos.dates?.length > 0 ? pos.dates.join(', ') : pos.date}
                          </span>
                          <span className="text-xs text-slate-500">•</span>
                          <span className="text-xs text-slate-500">{pos.start_time} - {pos.end_time}</span>
                        </div>
                      </div>
                      <Badge className={
                        pos.status === 'filled' ? 'bg-emerald-600' :
                        pos.status === 'open' ? 'bg-blue-600' :
                        'bg-slate-600'
                      }>
                        {pos.status}
                      </Badge>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </Card>
          </TabsContent>

          <TabsContent value="leads" className="space-y-4">
            <PricingLeadsTab />
          </TabsContent>

          <TabsContent value="trials" className="space-y-4">
            <TrialPremiumTab />
          </TabsContent>
        </Tabs>
      </main>

      {/* User CRUD Dialogs */}
      <CreateUserDialog
        open={showCreateUser}
        onOpenChange={setShowCreateUser}
        form={userForm}
        setForm={setUserForm}
        onSubmit={handleCreateUser}
        isSubmitting={isSubmitting}
      />
      
      <EditUserDialog
        open={showEditUser}
        onOpenChange={setShowEditUser}
        form={editUserForm}
        setForm={setEditUserForm}
        onSubmit={handleUpdateUser}
        isSubmitting={isSubmitting}
      />
      
      <ResetPasswordDialog
        open={showResetPassword}
        onOpenChange={setShowResetPassword}
        newPassword={newPassword}
        setNewPassword={setNewPassword}
        onSubmit={handleResetPassword}
        isSubmitting={isSubmitting}
      />
      
      <DeleteUserDialog
        open={showDeleteUser}
        onOpenChange={setShowDeleteUser}
        onConfirm={handleDeleteUser}
        isSubmitting={isSubmitting}
      />
      
      <UserDetailDialog
        open={showUserDetail}
        onOpenChange={setShowUserDetail}
        user={selectedUser}
      />

      {/* School Detail Modal */}
      <Dialog open={showSchoolDetail} onOpenChange={setShowSchoolDetail}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">School Details</DialogTitle>
          </DialogHeader>
          {selectedSchool && (
            <div className="space-y-4">
              <div>
                <p className="text-xl font-semibold">{selectedSchool.name}</p>
                <p className="text-slate-400">{selectedSchool.address}</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-700/50 rounded-lg p-3">
                  <p className="text-xs text-slate-400">School Code</p>
                  <p className="text-lg font-mono">{selectedSchool.school_code}</p>
                </div>
                <div className="bg-slate-700/50 rounded-lg p-3">
                  <p className="text-xs text-slate-400">Status</p>
                  <Badge className={selectedSchool.archived ? 'bg-slate-600' : 'bg-emerald-600'}>
                    {selectedSchool.archived ? 'Archived' : 'Active'}
                  </Badge>
                </div>
              </div>
              
              <div className="bg-slate-700/50 rounded-lg p-4 space-y-2">
                <p className="text-sm font-medium">Contact Information</p>
                <p className="text-sm text-slate-400">Name: <span className="text-white">{selectedSchool.contact_person}</span></p>
                {selectedSchool.contact_position && (
                  <p className="text-sm text-slate-400">Position: <span className="text-white">{selectedSchool.contact_position}</span></p>
                )}
                {selectedSchool.contact_email && (
                  <p className="text-sm text-slate-400">Email: <span className="text-white">{selectedSchool.contact_email}</span></p>
                )}
                {selectedSchool.contact_phone && (
                  <p className="text-sm text-slate-400">Phone: <span className="text-white">{selectedSchool.contact_phone}</span></p>
                )}
              </div>
              
              <div className="bg-slate-700/50 rounded-lg p-4 space-y-2">
                <p className="text-sm font-medium">Statistics</p>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <p className="text-slate-400">Connected Subs: <span className="text-white">{selectedSchool.toc_list?.length || 0}</span></p>
                  <p className="text-slate-400">Pending Requests: <span className="text-white">{selectedSchool.pending_tocs?.length || 0}</span></p>
                  <p className="text-slate-400">Total Positions: <span className="text-white">{selectedSchool.position_stats?.total || 0}</span></p>
                  <p className="text-slate-400">Filled: <span className="text-emerald-400">{selectedSchool.position_stats?.filled || 0}</span></p>
                </div>
              </div>
              
              {selectedSchool.employer && (
                <div className="bg-slate-700/50 rounded-lg p-4">
                  <p className="text-sm font-medium mb-2">Owner</p>
                  <p className="text-sm">{selectedSchool.employer.name}</p>
                  <p className="text-xs text-slate-400">{selectedSchool.employer.email}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
